import { Filesystem, Directory } from '@capacitor/filesystem';
import { OFFLINE_STORES, getOfflineStorageDriver } from './offlineStorageAdapter.js';
import { createOfflineAttachmentQueue } from './offlineAttachmentQueue.js';

const QUEUE_CHANGED_EVENT = 'visita-offline-media-queue-changed';
const DB_NAME = 'coordinacion_visitas_offline_media';
const DB_VERSION = 1;
const STORE_NAME = 'visita_offline_media_entries';

let dbPromise = null;

function toNumber(value) {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
}

function emitQueueChanged() {
    if (typeof window === 'undefined') {
        return;
    }

    window.dispatchEvent(new CustomEvent(QUEUE_CHANGED_EVENT));
}

function openDb() {
    if (dbPromise) {
        return dbPromise;
    }

    if (typeof window === 'undefined' || typeof window.indexedDB === 'undefined') {
        dbPromise = Promise.resolve(null);
        return dbPromise;
    }

    dbPromise = new Promise((resolve) => {
        try {
            const request = window.indexedDB.open(DB_NAME, DB_VERSION);

            request.onupgradeneeded = () => {
                const db = request.result;
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    const store = db.createObjectStore(STORE_NAME, {
                        keyPath: 'id',
                    });
                    store.createIndex('by_user', 'userId', { unique: false });
                    store.createIndex('by_visita', 'visitaId', { unique: false });
                    store.createIndex('by_created_at', 'createdAt', { unique: false });
                }
            };

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => resolve(null);
        } catch {
            resolve(null);
        }
    });

    return dbPromise;
}

function withStore(db, mode, operation) {
    return new Promise((resolve, reject) => {
        try {
            const tx = db.transaction(STORE_NAME, mode);
            const store = tx.objectStore(STORE_NAME);
            operation(store, resolve, reject);
        } catch (error) {
            reject(error);
        }
    });
}

function sortByCreatedAt(entries) {
    return [...entries].sort((a, b) => {
        const tsA = Date.parse(a?.createdAt || '') || 0;
        const tsB = Date.parse(b?.createdAt || '') || 0;
        return tsA - tsB;
    });
}

export async function enqueueVisitaOfflineImage({ userId, empresaId, visitaId, file, descripcion = '', metadata = {}, originRevision = null }) {
    if (!file) return null;
    const queue = createOfflineAttachmentQueue({ fs: Filesystem, directory: Directory.Data, driver: getOfflineStorageDriver() });
    const entry = await queue.enqueue({ userId: Number(userId), empresaId: Number(empresaId), visitaId: Number(visitaId), file, descripcion, metadata, originRevision });
    emitQueueChanged();
    return entry;
}

export async function enqueueVisitaOfflineDocument({ userId, empresaId, visitaId, file, acceptanceRole }) {
    const entry = await createOfflineAttachmentQueue({ fs: Filesystem, directory: Directory.Data, driver: getOfflineStorageDriver() }).enqueue({
        userId: Number(userId), empresaId: Number(empresaId), visitaId: Number(visitaId), file, metadata: acceptanceRole ? {acceptanceRole} : {}, kind: 'document' });
    emitQueueChanged();
    return entry;
}

export async function getOfflineDocumentEntriesByVisita(visitaId, userId, empresaId) {
    const driver = getOfflineStorageDriver();
    const [entries, files] = await Promise.all([getOfflineImageEntriesByVisita(visitaId, userId), driver.getAll(OFFLINE_STORES.FILES, null, { userId: Number(userId) })]);
    const ids = new Set(files.filter(row => row.kind === 'document' && row.userId === Number(userId) && row.empresaId === Number(empresaId) && row.visitaId === Number(visitaId)).map(row => row.id));
    return entries.filter(entry => ids.has(entry.id));
}

export async function sendOfflineImageEntry(options) {
    const result = await createOfflineAttachmentQueue({ fs: Filesystem, directory: Directory.Data, driver: getOfflineStorageDriver() }).send({ ...options, upload: options.uploadItem });
    if (result.acknowledged) emitQueueChanged();
    return result;
}

export async function recoverOfflineImageFiles({ driver = getOfflineStorageDriver() } = {}) {
    return createOfflineAttachmentQueue({ fs: Filesystem, directory: Directory.Data, driver }).recover();
}

export async function getOfflineImageEntriesByUser(userId, { visitaId } = {}) {
    const normalizedUserId = toNumber(userId);

    const driver = getOfflineStorageDriver();
    try {
        const allMedia = await driver.getAll(OFFLINE_STORES.MEDIA, null, { userId: normalizedUserId, ...(visitaId===undefined?{}:{visitaId}) });
        if (Array.isArray(allMedia)) {
            const filtered = allMedia.filter((entry) => {
                if(visitaId!==undefined&&toNumber(entry?.visitaId)!==visitaId)return false;
                if (normalizedUserId === null) {
                    return true;
                }
                return toNumber(entry?.userId) === normalizedUserId;
            });
            return sortByCreatedAt(filtered);
        }
    } catch (driverError) {
        if (driver?.strict || driver?.effectiveEngine === 'sqlite' || (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform?.())) {
            console.error('[visitaOfflineMediaQueue] Error crítico en SQLite al leer imágenes; prohibido degradar a IndexedDB:', driverError);
            throw driverError;
        }
    }

    const db = await openDb();
    if (!db) {
        return [];
    }

    const entries = await withStore(db, 'readonly', (store, resolve, reject) => {
        const request = store.getAll();
        request.onsuccess = () => resolve(Array.isArray(request.result) ? request.result : []);
        request.onerror = () => reject(request.error);
    });

    const filtered = entries.filter((entry) => {
        if (normalizedUserId === null) {
            return true;
        }
        return toNumber(entry?.userId) === normalizedUserId;
    });

    return sortByCreatedAt(filtered);
}

export async function getOfflineImagePendingCountByUser(userId) {
    const entries = await getOfflineImageEntriesByUser(userId);
    return entries.length;
}

export async function getOfflineImageEntriesByVisita(visitaId, userId = null) {
    if (!userId) {
        return [];
    }
    const targetVisitaId = toNumber(visitaId);
    if(!Number.isSafeInteger(targetVisitaId)||targetVisitaId<1)return [];
    const entries = await getOfflineImageEntriesByUser(userId,{visitaId:targetVisitaId});
    return entries.filter((entry) => toNumber(entry.visitaId) === targetVisitaId);
}

export async function removeOfflineImageEntryById(id, options = {}) {
    if (!id) {
        return;
    }

    const driver = getOfflineStorageDriver();
    try {
        const entry = await driver.get(OFFLINE_STORES.MEDIA, id);
        if (entry) {
            // El journal se retira solo por recibo exacto o un protocolo de descarte.
            // Un botón genérico o un completado legacy no autoriza borrar sus bytes.
            if (entry.filePath?.startsWith('offline-files/') || await driver.get(OFFLINE_STORES.FILES, id)) return false;
            await driver.delete(OFFLINE_STORES.MEDIA, id);
            if (options.deleteFile !== false && entry?.filePath) {
                try {
                    await Filesystem.deleteFile({
                        path: entry.filePath,
                        directory: Directory.Data,
                    });
                } catch {
                    // Ignorar si el archivo no existe
                }
            }
            emitQueueChanged();
            return;
        }
        if (driver?.strict || driver?.effectiveEngine === 'sqlite' || (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform?.())) {
            return;
        }
    } catch (driverError) {
        if (driver?.strict || driver?.effectiveEngine === 'sqlite' || (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform?.())) {
            console.error('[visitaOfflineMediaQueue] Error crítico en SQLite al eliminar imagen; prohibido degradar a IndexedDB:', driverError);
            throw driverError;
        }
    }

    const db = await openDb();
    if (!db) {
        return;
    }

    const entry = await withStore(db, 'readonly', (store, resolve, reject) => {
        const request = store.get(id);
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => reject(request.error);
    });

    await withStore(db, 'readwrite', (store, resolve, reject) => {
        const request = store.delete(id);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });

    if (options.deleteFile !== false && entry?.filePath) {
        try {
            await Filesystem.deleteFile({
                path: entry.filePath,
                directory: Directory.Data,
            });
        } catch {
            // Ignorar si el archivo ya no existe.
        }
    }

    emitQueueChanged();
}

export async function updateOfflineImageMetadata(id, metadataUpdater) {
    if (!id) {
        return null;
    }

    const driver = getOfflineStorageDriver();
    try {
        const entry = await driver.get(OFFLINE_STORES.MEDIA, id);
        if (entry) {
            const journal = await driver.get(OFFLINE_STORES.FILES, id);
            if (journal?.uploadCommand || journal?.state === 'acknowledged') throw Error('La foto ya tiene un envío preparado. Se conserva para revisión.');
            const currentMeta = entry.metadata || {};
            const nextMeta = typeof metadataUpdater === 'function'
                ? metadataUpdater(currentMeta)
                : { ...currentMeta, ...metadataUpdater };

            const modified = {
                ...entry,
                metadata: nextMeta,
                updatedAt: new Date().toISOString(),
            };

            await driver.put(OFFLINE_STORES.MEDIA, modified);
            emitQueueChanged();
            return modified;
        }
        if (driver?.strict || driver?.effectiveEngine === 'sqlite' || (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform?.())) {
            return null;
        }
    } catch (driverError) {
        if (driver?.strict || driver?.effectiveEngine === 'sqlite' || (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform?.())) {
            console.error('[visitaOfflineMediaQueue] Error crítico en SQLite al actualizar metadata; prohibido degradar a IndexedDB:', driverError);
            throw driverError;
        }
    }

    const db = await openDb();
    if (!db) {
        return null;
    }

    const updated = await withStore(db, 'readwrite', (store, resolve, reject) => {
        const request = store.get(id);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
            const entry = request.result;
            if (!entry) {
                resolve(null);
                return;
            }

            const currentMeta = entry.metadata || {};
            const nextMeta = typeof metadataUpdater === 'function'
                ? metadataUpdater(currentMeta)
                : { ...currentMeta, ...metadataUpdater };

            const modified = {
                ...entry,
                metadata: nextMeta,
                updatedAt: new Date().toISOString(),
            };

            const putRequest = store.put(modified);
            putRequest.onsuccess = () => resolve(modified);
            putRequest.onerror = () => reject(putRequest.error);
        };
    });

    if (updated) {
        emitQueueChanged();
    }
    return updated;
}

export async function flushOfflineImageQueue({ userId, empresaId, isCurrent, getRevisions, uploadItem, onAcknowledged, visitIds = null, allowedFileIds = null }) {
    if (typeof uploadItem !== 'function') {
        return { synced: 0, failed: 0 };
    }

    const entries = await getOfflineImageEntriesByUser(userId);
    let synced = 0;
    let failed = 0;
    const errors = [];

    for (const entry of entries) {
        if (visitIds && !visitIds.includes(Number(entry.visitaId))) continue;
        if (allowedFileIds && !allowedFileIds.includes(entry.id)) continue;
        if (isCurrent && !isCurrent()) return { synced, failed, errors, identityChanged: true };
        if (!entry?.filePath || !entry?.visitaId) {
            failed += 1;
            errors.push({ id: entry?.id, message: 'La foto pendiente no tiene archivo o visita asociados. Datos conservados.' });
            continue;
        }

        try {
            const driver = getOfflineStorageDriver();
            const journal = await driver.get(OFFLINE_STORES.FILES, entry.id);
            if (journal || entry.filePath.startsWith('offline-files/')) {
                const result = await createOfflineAttachmentQueue({ fs: Filesystem, directory: Directory.Data, driver }).send({ entry,
                    userId: Number(userId), empresaId: Number(empresaId), isCurrent, getRevisions, upload: uploadItem });
                if (result?.unauthorized) return { synced, failed, errors, unauthorized: true };
                if (result?.acknowledged !== true) {
                    failed++;
                    errors.push({ id: entry.id, message: result?.message || 'La foto espera resolver sus dependencias. Archivo conservado.' });
                    continue;
                }
                synced++;
                emitQueueChanged();
                if (typeof onAcknowledged === 'function') await onAcknowledged(result);
                continue;
            }
            // Un registro histórico sin journal no acredita propietario, manifiesto
            // ni revisión de los bytes. No transmitir ni retirar por un success genérico.
            failed++;
            errors.push({id:entry.id,message:'El archivo histórico necesita recuperar su comprobante y propietario. Se conserva íntegro, sin reenviar ni borrar.'});
            continue;
        } catch (itemError) {
            if (itemError?.response?.status === 401 || itemError?.unauthorized === true) {
                return { synced, failed, errors, unauthorized: true };
            }
            failed += 1;
            errors.push({ id: entry.id, message: String(itemError?.response?.data?.error || itemError?.response?.data?.message || itemError?.message || 'No se pudo enviar la foto. Archivo conservado.') });
        }
    }

    return { synced, failed, errors };
}

export function subscribeOfflineImageQueueChanges(listener) {
    if (typeof window === 'undefined' || typeof listener !== 'function') {
        return () => {};
    }

    window.addEventListener(QUEUE_CHANGED_EVENT, listener);
    return () => window.removeEventListener(QUEUE_CHANGED_EVENT, listener);
}
